import { useQueryClient, useSuspenseQueries } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ScrollPositionAnchor } from "../../../../components/scrollPosition/ScrollPositionAnchor";
import { SectionFieldSet } from "../../../../components/sectionFieldSet/SectionFieldSet";
import { T } from "../../../../components/translation/T";
import { dataDocuments } from "../../../../data/dataDocuments";
import { DocumentId } from "../../../../data/models/CommonTypes";
import { Status } from "../../../../data/types";
import { useContractEditStatus } from "../../../../hooks/useContractEditStatus";
import { useContractId } from "../../../../hooks/useContractId";
import { LinkAnchors } from "../ContractEdit";
import { FormName } from "../menus/ContractEditMenu";
import { AddCustomDoc } from "./AddCustomDoc";
import { FileRow } from "../../../../components/fileUpload/FileRow";
import { dataContracts } from "../../../../data/dataContracts";
import { isContractDisabled } from "../../../../App";
import "./Uploads.scss";
import {
  ContractDocument,
  DocumentType,
} from "../../../../data/models/ContractTypes";

const filteredDocs: DocumentType[] = [
  DocumentType.SIGNED_CONTRACT,
  DocumentType.BANK_STATEMENT,
  DocumentType.APP_SCREENSHOT,
  DocumentType.CONTROL_STRUCTURE,
  DocumentType.RECORD_OF_NOMINEE_SHAREHOLDERS,
  DocumentType.LETTER_OF_SHAREHOLDER_CUSTODIAN,
  DocumentType.ECOM_APPLICATION_SCREENSHOT,
];

export const Uploads: React.FunctionComponent<{ id: number }> = ({ id }) => {
  return (
    <SectionFieldSet
      headerTitle={LinkAnchors.UPLOADS.name}
      formName={FormName.UPLOADS}
      sectionNumber={id}
    >
      <UploadsInner />
    </SectionFieldSet>
  );
};

function UploadsInner() {
  const [status, setStatus] = useState<Status>(Status.DEFAULT);

  const [docs, setDocs] = useState<ContractDocument[]>([]);

  const contractId = useContractId();
  const [{ data: contract }, { data: documents }] = useSuspenseQueries({
    queries: [
      dataContracts.fetchContract(contractId),
      dataDocuments.fetchDocuments(contractId),
    ],
  });

  useEffect(() => {
    setDocs(
      documents.filter((doc) => !filteredDocs.includes(doc.documentType))
    );
  }, [documents]);

  const queryClient = useQueryClient();
  const inputStatus = useContractEditStatus();
  const disabled = isContractDisabled(contract);

  function onRemove(docId: DocumentId) {
    dataDocuments
      .removeCustomDocument(contractId, docId)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: dataDocuments.getDocumentsKey(contractId),
        });
      })
      .catch((err) => {
        setStatus(Status.ERROR);
        setTimeout(() => {
          setStatus(Status.DEFAULT);
        }, 4000);
      });
  }

  return (
    <div className="uploads">
      <ScrollPositionAnchor id={LinkAnchors.UPLOADS.anchor} />

      <p>
        <strong>
          <T>Documents required by merchant</T>
        </strong>
      </p>
      {docs.map((doc) => (
        <FileRow
          key={doc.documentId}
          status={status === Status.ERROR ? status : inputStatus}
          document={doc}
          onRemove={onRemove}
          disabled={disabled}
        />
      ))}
      {docs.length === 0 && (
        <p>
          <T>
            There are no required documents associated with this application
          </T>
        </p>
      )}
      <AddCustomDoc />
    </div>
  );
}
